import {
  deleteClipboardClip,
  getClipboardClips,
  saveClipboardClip,
  type ClipboardClip as Clip,
} from '@/services/clipboard.service';
import { APP_CLIPBOARD_APPEND, type AppClipboardRequest } from '@/utils/appClipboard';
import { ClipboardIcon, CopyIcon } from '@/utils/icons';
import { CloseOutlined, DownOutlined, MinusOutlined } from '@ant-design/icons';
import { Link, useIntl } from '@umijs/max';
import { Alert, Button, Input, message, Spin } from 'antd';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './index.less';

function appendUniqueClip(current: Clip[], clip: Clip): Clip[] {
  return current.some((item) => item.id === clip.id) ? current : [...current, clip];
}

function ClipContent({ clip, collapsed }: Readonly<{ clip: Clip; collapsed: boolean }>) {
  const body = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const element = body.current;
    if (!element) return;
    const measure = () => setHeight(element.scrollHeight);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [clip.text]);
  return (
    <div
      id={`angel-clipboard-content-${clip.id}`}
      className={`angel-clipboard-content${collapsed ? ' angel-clipboard-content-collapsed' : ''}`}
      style={{ maxHeight: collapsed ? 21 : height }}
    >
      <div ref={body} className="angel-clipboard-content-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
          {clip.text}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default function ClipboardPanel({
  pageName,
  href,
}: Readonly<{ pageName: string; href: string }>) {
  const intl = useIntl();
  const [clips, setClips] = useState<Clip[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const searchTerm = query.trim().toLowerCase();
  const visibleClips = clips.filter((clip) =>
    `${clip.pageName} ${clip.text}`.toLowerCase().includes(searchTerm),
  );
  const removed = useRef(new Set<string>());
  const active = useRef(true);
  const loadVersion = useRef(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [deleting, setDeleting] = useState<Set<string>>(() => new Set());
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const saved = await getClipboardClips();
      if (!active.current || version !== loadVersion.current) return;
      setClips((current) => [
        ...new Map(
          [...saved, ...current]
            .filter((clip) => !removed.current.has(clip.id))
            .map((clip) => [clip.id, clip]),
        ).values(),
      ]);
    } catch {
      if (active.current && version === loadVersion.current) setLoadFailed(true);
    } finally {
      if (active.current && version === loadVersion.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    active.current = true;
    void load();
    return () => {
      active.current = false;
      ++loadVersion.current;
    };
  }, [load]);
  const removeClip = async (id: string) => {
    setDeleting((current) => new Set(current).add(id));
    try {
      await deleteClipboardClip(id);
      if (!active.current) return;
      removed.current.add(id);
      setClips((current) => current.filter((clip) => clip.id !== id));
    } catch {
      if (active.current)
        message.error(
          intl.formatMessage({
            id: 'pages.clipboard.deleteFailed',
            defaultMessage: 'Could not remove the stack. Please try again.',
          }),
        );
    } finally {
      if (active.current)
        setDeleting((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
    }
  };
  const stack = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stack.current) stack.current.scrollTop = 0;
  }, [query]);
  useEffect(() => {
    if (stack.current) stack.current.scrollTop = stack.current.scrollHeight;
  }, [clips, open]);
  const title = intl.formatMessage({
    id: 'components.appShell.clipboard.title',
    defaultMessage: 'Clipboard',
  });
  const persistClip = useCallback(
    async (id: string, input: Parameters<typeof saveClipboardClip>[1]) => {
      try {
        const clip = await saveClipboardClip(id, input);
        if (active.current) setClips((current) => appendUniqueClip(current, clip));
        return true;
      } catch {
        return false;
      } finally {
        if (active.current) setSaving((count) => count - 1);
      }
    },
    [],
  );
  useEffect(() => {
    const append = (event: Event) => {
      const request = (event as CustomEvent<AppClipboardRequest>).detail;
      if (typeof request?.text !== 'string' || !request.text.trim()) return;
      const id = crypto.randomUUID();
      const input = { text: request.text, pageName, href };
      setSaving((count) => count + 1);
      request.completion = persistClip(id, input);
      setOpen(true);
      setQuery('');
      request.handled = true;
    };
    window.addEventListener(APP_CLIPBOARD_APPEND, append);
    return () => window.removeEventListener(APP_CLIPBOARD_APPEND, append);
  }, [pageName, href, persistClip]);

  let countMessage = { id: 'pages.clipboard.totalCount', defaultMessage: '{count} stacks' };
  if (searchTerm) {
    countMessage = {
      id: 'pages.clipboard.filteredCount',
      defaultMessage: '{visible} of {total} stacks',
    };
  } else if (clips.length === 1) {
    countMessage = { id: 'pages.clipboard.singleCount', defaultMessage: '{count} stack' };
  }
  const toggleCollapsed = (id: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!open) {
    return (
      <Button
        className="angel-clipboard-trigger"
        type="primary"
        size="large"
        aria-label={title}
        icon={<ClipboardIcon />}
        onClick={() => setOpen(true)}
      />
    );
  }
  return (
    <section className="angel-clipboard" aria-label={title}>
      <header className="angel-clipboard-heading">
        <h2>
          <ClipboardIcon />
          {title}
        </h2>
        <div className="angel-clipboard-actions">
          <Button
            type="text"
            icon={<CopyIcon />}
            disabled={!clips.length}
            aria-label={intl.formatMessage({
              id: 'pages.clipboard.copyAll',
              defaultMessage: 'Copy all clips',
            })}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(clips.map((clip) => clip.text).join('\n\n'));
                message.success(
                  intl.formatMessage({
                    id: 'components.appShell.clipboard.copiedMessage',
                    defaultMessage: 'Copied!',
                  }),
                );
              } catch {
                message.error(
                  intl.formatMessage({
                    id: 'pages.chat.selection.copyFailed',
                    defaultMessage: 'Could not copy the selection',
                  }),
                );
              }
            }}
          />
          <Button
            type="text"
            icon={<MinusOutlined />}
            aria-label={intl.formatMessage({
              id: 'pages.clipboard.close',
              defaultMessage: 'Close Clipboard',
            })}
            onClick={() => setOpen(false)}
          />
        </div>
      </header>
      <div className="angel-clipboard-search">
        <label htmlFor="angel-clipboard-search-input">
          {intl.formatMessage({ id: 'pages.clipboard.search', defaultMessage: 'Clipboard Search' })}
        </label>
        <Input
          id="angel-clipboard-search-input"
          allowClear
          value={query}
          placeholder={intl.formatMessage({
            id: 'pages.clipboard.searchPlaceholder',
            defaultMessage: 'Search all clips',
          })}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <p className="angel-clipboard-count" aria-live="polite" aria-atomic="true">
        {intl.formatMessage(countMessage, {
          count: clips.length,
          visible: visibleClips.length,
          total: clips.length,
        })}
      </p>
      <div className="angel-clipboard-stack" ref={stack}>
        {(loading || saving > 0) && (
          <div className="angel-clipboard-loading">
            <Spin size="small" />
          </div>
        )}
        {loadFailed && (
          <Alert
            type="error"
            showIcon
            message={intl.formatMessage({
              id: 'pages.clipboard.loadFailed',
              defaultMessage: 'Could not load your saved stacks.',
            })}
            action={
              <Button size="small" onClick={() => void load()}>
                {intl.formatMessage({ id: 'pages.clipboard.retry', defaultMessage: 'Retry' })}
              </Button>
            }
          />
        )}
        {!loading && !saving && !loadFailed && !clips.length && (
          <p className="angel-clipboard-empty">
            {intl.formatMessage({
              id: 'pages.clipboard.empty',
              defaultMessage: 'Select text and choose Copy to Clipboard to collect it here.',
            })}
          </p>
        )}
        {!!clips.length && !visibleClips.length && (
          <output className="angel-clipboard-empty">
            {intl.formatMessage({
              id: 'pages.clipboard.noMatches',
              defaultMessage: 'No clips match your search.',
            })}
          </output>
        )}
        {visibleClips.map((clip) => (
          <article className="angel-clipboard-clip" key={clip.id}>
            <header className="angel-clipboard-clip-heading">
              <span className="angel-clipboard-dot" aria-hidden="true" />
              <span className="angel-clipboard-page">{clip.pageName}</span>
              <Link to={clip.href}>
                {intl.formatMessage({ id: 'pages.clipboard.goTo', defaultMessage: 'Go to it' })}
              </Link>
              <Button
                className="angel-clipboard-toggle"
                type="text"
                size="small"
                icon={
                  <DownOutlined
                    className={collapsed.has(clip.id) ? 'angel-clipboard-chevron-collapsed' : ''}
                  />
                }
                aria-expanded={!collapsed.has(clip.id)}
                aria-controls={`angel-clipboard-content-${clip.id}`}
                aria-label={intl.formatMessage(
                  collapsed.has(clip.id)
                    ? { id: 'pages.clipboard.expand', defaultMessage: 'Expand clip' }
                    : { id: 'pages.clipboard.collapse', defaultMessage: 'Collapse clip' },
                )}
                onClick={() => toggleCollapsed(clip.id)}
              />
              <Button
                className="angel-clipboard-remove"
                type="text"
                size="small"
                icon={<CloseOutlined />}
                aria-label={intl.formatMessage({
                  id: 'pages.clipboard.remove',
                  defaultMessage: 'Remove clip',
                })}
                loading={deleting.has(clip.id)}
                onClick={() => void removeClip(clip.id)}
              />
            </header>
            <ClipContent clip={clip} collapsed={collapsed.has(clip.id)} />
          </article>
        ))}
      </div>
    </section>
  );
}
